/**
 * Recompressão de imagem no navegador (canvas → JPEG) antes de subir.
 *
 * Foto de celular sai com 3–12 MB; o OCR autenticado vai em JSON base64 e
 * o parser JSON do servidor aceita no máximo 1 MB. Reduzir aqui também
 * deixa o envio rápido em 4G fraco de oficina.
 */

export type OpcoesCompressao = {
  /** Maior lado da imagem final, em px. Imagens menores não são ampliadas. */
  maxLado: number;
  /** Qualidade JPEG, 0–1. */
  qualidade: number;
};

/**
 * Leitura de placa/KM: mais pixels que as fotos do veículo, porque a foto
 * da galeria costuma ter sido tirada de longe e a placa ocupa pouco dela.
 * Se passar do limite do JSON, `prepararImagemOcr` desce de degrau.
 */
export const COMPRESSAO_OCR: OpcoesCompressao = { maxLado: 2048, qualidade: 0.85 };

/** O servidor recusa base64 acima de ~950 KB no OCR autenticado. */
export const LIMITE_BASE64_OCR = 950_000;

export type Desenhavel = {
  fonte: CanvasImageSource;
  largura: number;
  altura: number;
  liberar: () => void;
};

export async function decodificar(arquivo: Blob): Promise<Desenhavel> {
  // `imageOrientation: 'from-image'` aplica o EXIF: foto de celular em pé
  // não sai deitada. Navegadores antigos não têm createImageBitmap (ou não
  // aceitam a opção) — aí o <img>, que já respeita EXIF nos navegadores
  // atuais, resolve.
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(arquivo, { imageOrientation: 'from-image' });
      return {
        fonte: bitmap,
        largura: bitmap.width,
        altura: bitmap.height,
        liberar: () => bitmap.close(),
      };
    } catch {
      // segue para o fallback
    }
  }
  const url = URL.createObjectURL(arquivo);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Não foi possível abrir a imagem.'));
      el.src = url;
    });
    return {
      fonte: img,
      largura: img.naturalWidth || img.width,
      altura: img.naturalHeight || img.height,
      liberar: () => URL.revokeObjectURL(url),
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/** Dimensões finais mantendo a proporção, sem ampliar. */
export function calcularDimensoes(
  largura: number,
  altura: number,
  maxLado: number,
): { largura: number; altura: number } {
  const maior = Math.max(largura, altura);
  if (maior <= 0) return { largura: 0, altura: 0 };
  const escala = Math.min(1, maxLado / maior);
  return {
    largura: Math.max(1, Math.round(largura * escala)),
    altura: Math.max(1, Math.round(altura * escala)),
  };
}

export async function comprimirImagem(arquivo: Blob, opcoes: OpcoesCompressao): Promise<Blob> {
  const img = await decodificar(arquivo);
  try {
    const { largura, altura } = calcularDimensoes(img.largura, img.altura, opcoes.maxLado);
    if (!largura || !altura) throw new Error('A imagem está vazia.');
    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = altura;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Não foi possível processar a imagem neste aparelho.');
    // PNG com transparência viraria fundo preto no JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, largura, altura);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img.fonte, 0, 0, largura, altura);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', opcoes.qualidade),
    );
    if (!blob) throw new Error('Não foi possível processar a imagem neste aparelho.');
    return blob;
  } finally {
    img.liberar();
  }
}

/** Conteúdo do blob em base64 puro — sem o prefixo `data:image/...;base64,`. */
export function blobParaBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => {
      const resultado = typeof leitor.result === 'string' ? leitor.result : '';
      const virgula = resultado.indexOf(',');
      resolve(virgula >= 0 ? resultado.slice(virgula + 1) : resultado);
    };
    leitor.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    leitor.readAsDataURL(blob);
  });
}

/**
 * Prepara a foto para o OCR: comprime no padrão (2048px, 0.85) e, se ainda
 * passar do limite do servidor, tenta de novo menor antes de desistir —
 * primeiro baixando a qualidade, que custa menos à leitura que a resolução.
 */
export async function prepararImagemOcr(arquivo: Blob): Promise<string> {
  const tentativas: OpcoesCompressao[] = [
    COMPRESSAO_OCR,
    { maxLado: 2048, qualidade: 0.72 },
    { maxLado: 1600, qualidade: 0.78 },
    { maxLado: 1280, qualidade: 0.8 },
    { maxLado: 1024, qualidade: 0.72 },
    { maxLado: 800, qualidade: 0.65 },
  ];
  for (const opcoes of tentativas) {
    const base64 = await blobParaBase64(await comprimirImagem(arquivo, opcoes));
    if (base64.length <= LIMITE_BASE64_OCR) return base64;
  }
  throw new Error('A foto ficou grande demais para enviar. Tente fotografar mais de perto.');
}
